"use client";

import { useState, useMemo } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@stockline/ui/components/button";
import { Input } from "@stockline/ui/components/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";

export function GroupField({
  groupId,
  groups,
  onChange,
}: {
  readonly groupId: string | null;
  readonly groups: Array<{ id: string | number; name: string }>;
  readonly onChange: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedGroup = groups.find((g) => String(g.id) === groupId);

  const filtered = useMemo(() => {
    if (!search) return groups;
    const q = search.toLowerCase();
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, search]);

  return (
    <div className="px-3 pb-2">
      <div className="text-xs text-muted-foreground mb-1.5">
        {t("ai.userInput.selectGroup", "Groupe")}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-sm h-8"
            data-testid="group-select-trigger"
          >
            <span className="truncate">
              {selectedGroup?.name ??
                t("ai.userInput.searchGroup", "Choisir un groupe...")}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <div className="p-2 border-b">
            <Input
              autoFocus
              placeholder={t("ai.userInput.searchGroup", "Rechercher...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 text-sm"
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                {t("ai.userInput.noGroups", "Aucun groupe disponible")}
              </div>
            )}
            {filtered.map((group) => (
              <button
                key={group.id}
                type="button"
                onClick={() => {
                  onChange(String(group.id));
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 rounded px-2 py-1.5 text-sm text-left hover:bg-accent ${
                  String(group.id) === groupId ? "bg-accent font-medium" : ""
                }`}
                data-testid={`group-option-${group.id}`}
              >
                {String(group.id) === groupId && (
                  <Check className="h-3 w-3 shrink-0" />
                )}
                <span className="truncate">{group.name}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
